import React, { useState } from "react";
import BaseModal from "../BaseModal";
import { mdiCashPlus } from "@mdi/js";
import { toast } from "react-toastify";
import { classApi } from "../../api/classes";
import "./ManualChargeModal.css";

/**
 * Mini-form to create a flat admin-asserted manual charge against a student
 * (no package binding). Part of the per-student Payments audit tab.
 * amount is entered in DOLLARS; the backend converts to integer cents.
 */
function ManualChargeModal({ isOpen, onClose, studentProfileId, studentName, currency, enrolmentId, onSuccess }) {
    const [amount, setAmount] = useState("");
    const [reason, setReason] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const reset = () => {
        setAmount("");
        setReason("");
    };

    const handleClose = () => {
        if (isSaving) return;
        reset();
        onClose();
    };

    const handleSubmit = async () => {
        const amt = parseFloat(amount);
        if (!Number.isFinite(amt) || amt <= 0) {
            toast.error("Enter an amount greater than zero.");
            return;
        }
        if (!reason.trim()) {
            toast.error("Enter a reason for this charge.");
            return;
        }
        setIsSaving(true);
        try {
            const res = await classApi.createManualCharge(studentProfileId, amt, reason.trim(), enrolmentId);
            if (res.ok) {
                toast.success("Charge added.");
                reset();
                if (onSuccess) onSuccess();
            } else {
                toast.error(res.data?.message || "Failed to add charge.");
            }
        } catch (err) {
            toast.error("Failed to add charge.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleClose}
            title={studentName ? `Add charge — ${studentName}` : "Add charge"}
            icon={mdiCashPlus}
            maxWidth={480}
            className="manual-charge-modal"
            footer={
                <>
                    <button className="base-modal-cancel-btn" onClick={handleClose} disabled={isSaving}>
                        Cancel
                    </button>
                    <button className="base-modal-save-btn" onClick={handleSubmit} disabled={isSaving}>
                        {isSaving ? "Adding..." : "Add charge"}
                    </button>
                </>
            }>
            <div className="manual-charge-body">
                <p className="manual-charge-intro">
                    A charge adds an amount the student owes that isn't tied to a class package (for example, a
                    balance carried over, a correction, a drop-in/walk-in one-off class, or school merchandise). It
                    appears on their ledger as outstanding until marked paid.
                </p>
                <div className="manual-charge-group">
                    <label htmlFor="manualChargeAmount" className="manual-charge-label">
                        Amount{currency ? ` (${currency})` : ""}
                    </label>
                    <p className="manual-charge-hint">The amount to add to the student's balance, in dollars.</p>
                    <input
                        type="text"
                        inputMode="decimal"
                        id="manualChargeAmount"
                        className="manual-charge-input"
                        value={amount}
                        onChange={e => {
                            // Reject anything that isn't a valid positive dollar amount
                            // (digits + a single decimal point, up to 2 places). Blocks letters,
                            // "e", "+", "-", and pasted text that type="number" would let through.
                            const v = e.target.value;
                            if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) setAmount(v);
                        }}
                        placeholder="0.00"
                        autoFocus
                    />
                </div>

                <div className="manual-charge-group">
                    <label htmlFor="manualChargeReason" className="manual-charge-label">
                        Reason
                    </label>
                    <p className="manual-charge-hint">Why this charge is being added (kept on the audit record).</p>
                    <input
                        type="text"
                        id="manualChargeReason"
                        className="manual-charge-input"
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        placeholder="e.g. Outstanding balance from last term"
                    />
                </div>

                <p className="manual-charge-hint manual-charge-refnote">
                    A payment reference code is generated automatically for reconciliation.
                </p>
            </div>
        </BaseModal>
    );
}

export default ManualChargeModal;
